import Phaser from 'phaser';
import { GameSettings } from '../utils/GameSettings';
import { SharedResources } from '../utils/SharedResources';

// Add an interface for URL parameters
export interface URLParams {
  portal?: boolean;
  ref?: string;
  username?: string;
}

export class BootScene extends Phaser.Scene {
  private originalRAF: (callback: FrameRequestCallback) => number;
  private currentFpsLimit: number = 60;
  private lastFrameTime: number = 0;
  private rafId: number = 0;
  
  // Static property to store URL parameters globally
  private static urlParams: URLParams = {};
  
  constructor() {
    super('BootScene');
    // Store the original requestAnimationFrame
    this.originalRAF = window.requestAnimationFrame;
    
    // Parse URL parameters at the start
    BootScene.parseUrlParams();
  }

  // Static method to parse URL parameters
  public static parseUrlParams(): URLParams {
    try {
      const urlParams = new URLSearchParams(window.location.search);
      
      // Parse parameters
      const portalParam = urlParams.get('portal');
      const refParam = urlParams.get('ref');
      const usernameParam = urlParams.get('username');
      
      // Store parameters
      this.urlParams = {
        portal: portalParam === 'true',
        ref: refParam || undefined,
        username: usernameParam || undefined
      };
      
      console.log('Global URL parameters parsed:', this.urlParams);
      return this.urlParams;
    } catch (error) {
      console.error('Error parsing URL parameters:', error);
      return {};
    }
  }
  
  // Static getter to access URL parameters from any scene
  public static getUrlParams(): URLParams {
    return this.urlParams;
  }

  preload(): void {
    // Apply FPS settings from localStorage at game startup
    const settingsManager = GameSettings.getInstance();
    const settings = settingsManager.getSettings();
    
    // Set up global FPS limiter by overriding requestAnimationFrame
    this.setupGlobalFpsLimiter(settings.fps);
    
    // Apply FPS setting to the game loop
    if (this.game && this.game.loop) {
      this.game.loop.targetFps = settings.fps;
      console.log(`Boot: Applied initial FPS setting: ${settings.fps}`);
    }

    // Create a loading text
    const loadingText = this.add.text(
      this.cameras.main.width / 2, 
      this.cameras.main.height / 2 - 50,
      'Loading...', 
      { 
        font: '20px Arial', 
        color: '#ffffff' 
      }
    ).setOrigin(0.5);
    
    // Load essential assets here
    // Initialize SharedResources for preloading
    const sharedResources = SharedResources.getInstance();
    sharedResources.initialize(this);
    
    // Preload all shared resources textures
    sharedResources.preloadMitochondrionTexture(this);
    sharedResources.preloadMitochondrionMinimalTexture(this);
    sharedResources.preloadLysosomeTexture(this);
    sharedResources.preloadGlucoseTexture(this);
    
    // Log which textures we're preloading for debugging
    console.log('Preloading all game textures in BootScene:');
    console.log('- Mitochondrion texture');
    console.log('- Minimal mitochondrion texture');
    console.log('- Lysosome texture');
    console.log('- Glucose texture');
    
    // Add loading bar for larger games
    const progressBar = this.add.graphics();
    progressBar.setData('isProgressBar', true);
    
    const progressBox = this.add.graphics();
    progressBox.setData('isProgressBox', true);
    
    progressBox.fillStyle(0x222222, 0.8);
    progressBox.fillRect(
      this.cameras.main.width / 2 - 160, 
      this.cameras.main.height / 2 - 25, 
      320, 
      50
    );
    
    this.load.on('progress', (value: number) => {
      progressBar.clear();
      progressBar.fillStyle(0xffffff, 1);
      progressBar.fillRect(
        this.cameras.main.width / 2 - 150, 
        this.cameras.main.height / 2 - 15, 
        300 * value, 
        30
      );
    });
    
    this.load.on('complete', () => {
      progressBar.destroy();
      progressBox.destroy();
      loadingText.destroy();
    });
    
    // Add resize event handler
    this.events.on('windowResized', this.handleWindowResize, this);
  }

  create(): void {
    // Make sure all textures are loaded before proceeding
    if (this.load.isLoading()) {
      console.log('Still loading textures, waiting to complete...');
      this.load.on('complete', () => {
        this.finalizeBootProcess();
      });
    } else {
      this.finalizeBootProcess();
    }
  }
  
  /**
   * Final steps after all resources are loaded
   */
  private finalizeBootProcess(): void {
    // Verify all critical textures are loaded
    const sharedResources = SharedResources.getInstance();
    const allTexturesLoaded = 
      sharedResources.hasMitochondrionTexture() &&
      sharedResources.hasMitochondrionMinimalTexture() &&
      sharedResources.hasLysosomeTexture() &&
      sharedResources.hasGlucoseTexture();
      
    if (!allTexturesLoaded) {
      console.warn('Not all textures were loaded, but proceeding anyway');
    } else {
      console.log('All critical textures successfully loaded');
    }
    
    // SharedResources is already initialized in preload
    console.log('SharedResources already initialized in BootScene');
    
    // Listen for fps rate changes
    this.events.on('fpsRateChanged', this.onFpsRateChanged, this);
    
    // Check if portal=true in URL parameters
    const urlParams = BootScene.getUrlParams();
    // if (urlParams.portal === true) {
    //   console.log('Portal parameter detected in URL, starting MultiplayerGameScene directly');
    //   // Start with empty roomId - the scene will handle the vibeverse room joining based on portal param
    //   this.scene.start('MultiplayerGameScene', { 
    //     roomId: '',
    //     playerName: urlParams.username || 'Player'
    //   });
    // } else {
    //   // Normal flow - go to main menu
    //   this.scene.start('MainMenuScene');
    // }
    // ! Temporary redirecting all to multiplayer
    this.scene.start('MultiplayerGameScene', { 
      roomId: '',
      playerName: urlParams.username || 'Player'
    });

  }
  
  /**
   * Handle FPS rate changes
   */
  onFpsRateChanged(fps: number): void {
    console.log(`BootScene received fpsRateChanged event: ${fps}`);
    this.updateFpsLimit(fps);
  }
  
  /**
   * Update the FPS limit
   */
  updateFpsLimit(fps: number): void {
    this.currentFpsLimit = fps;
    console.log(`Updated global FPS limiter to ${fps} FPS`);
  }
  
  /**
   * Set up a global FPS limiter by overriding requestAnimationFrame
   */
  setupGlobalFpsLimiter(fps: number): void {
    // Store the target FPS
    this.currentFpsLimit = fps;
    
    console.log(`Setting up global FPS limiter with target: ${fps} FPS`);
    
    // Create a throttled version of requestAnimationFrame
    const throttledRAF = (callback: FrameRequestCallback): number => {
      // Cancel any existing request
      if (this.rafId) {
        cancelAnimationFrame(this.rafId);
      }
      
      // Calculate the target frame duration
      const targetFrameDuration = 1000 / this.currentFpsLimit;
      
      // Schedule the next frame
      const scheduleFrame = () => {
        const now = performance.now();
        const elapsed = now - this.lastFrameTime;
        
        // If enough time has passed since the last frame, call the callback
        if (elapsed >= targetFrameDuration) {
          this.lastFrameTime = now;
          callback(now);
        } else {
          // Otherwise, wait for the remaining time
          const remainingTime = targetFrameDuration - elapsed;
          setTimeout(() => {
            // Request the next frame once the timeout completes
            this.rafId = this.originalRAF.call(window, scheduleFrame);
          }, remainingTime);
        }
      };
      
      // Start the first frame
      this.rafId = this.originalRAF.call(window, scheduleFrame);
      return this.rafId;
    };
    
    // Override the browser's requestAnimationFrame
    window.requestAnimationFrame = throttledRAF;
    
    console.log('Global FPS limiter installed');
  }
  
  /**
   * Clean up when the scene is shut down
   */
  shutdown(): void {
    // Restore the original requestAnimationFrame
    window.requestAnimationFrame = this.originalRAF;
    console.log('Restored original requestAnimationFrame');
    
    // Remove event listeners
    this.events.off('fpsRateChanged', this.onFpsRateChanged, this);
    this.events.off('windowResized', this.handleWindowResize);
    
    // Clean up any shared resources as this is the base scene
    SharedResources.getInstance().releaseAll();
  }

  /**
   * Handle window resize event
   */
  handleWindowResize(size: { width: number, height: number }): void {
    console.log(`BootScene handling window resize: ${size.width}x${size.height}`);
    
    // Update any UI elements that need to be repositioned
    // Find all text elements and graphics and center them
    this.children.list.forEach(child => {
      if (child instanceof Phaser.GameObjects.Text || 
          child instanceof Phaser.GameObjects.Graphics) {
        // Check if this is the loading text or progress elements
        const centerX = size.width / 2;
        const centerY = size.height / 2;
        
        if (child instanceof Phaser.GameObjects.Text && 
            child.text === 'Loading...') {
          // Reposition loading text
          child.setPosition(centerX, centerY - 50);
          console.log('Repositioned loading text');
        } else if (child instanceof Phaser.GameObjects.Graphics) {
          // Set name property for graphics objects when created to identify them
          const isProgressBox = child.getData('isProgressBox');
          const isProgressBar = child.getData('isProgressBar');
          
          // Reposition progress bar/box
          if (isProgressBox) {
            child.clear();
            child.fillStyle(0x222222, 0.8);
            child.fillRect(
              centerX - 160, 
              centerY - 25, 
              320, 
              50
            );
            console.log('Repositioned progress box');
          } else if (isProgressBar) {
            const progress = this.load.progress;
            child.clear();
            child.fillStyle(0xffffff, 1);
            child.fillRect(
              centerX - 150, 
              centerY - 15, 
              300 * progress, 
              30
            );
            console.log('Repositioned progress bar');
          }
        }
      }
    });
    
    // Ensure the camera is updated
    this.cameras.main.centerOn(size.width / 2, size.height / 2);
  }
} 